import { CategoryDivider } from '@affine/core/modules/app-sidebar/views';
import {
  type NavigationPanelRootSection,
  NavigationPanelService,
} from '@affine/core/modules/navigation-panel';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type CSSProperties,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
  useCallback,
} from 'react';

import { content, header, root } from './collapsible-section.css';
import { useRegisterNavigationPanelAccordionSection } from './navigation-accordion';

interface CollapsibleSectionProps extends PropsWithChildren {
  path: string[];
  title: string;
  actions?: ReactNode;

  className?: string;
  testId?: string;

  headerRef?: RefObject<HTMLDivElement>;
  headerTestId?: string;
  headerClassName?: string;

  contentClassName?: string;
  contentStyle?: CSSProperties;
  accordion?: boolean;
}

export const CollapsibleSection = ({
  path,
  title,
  actions,
  children,

  className,
  testId,

  headerRef,
  headerTestId,
  headerClassName,

  contentClassName,
  contentStyle,
  accordion = false,
}: CollapsibleSectionProps) => {
  const navigationPanelService = useService(NavigationPanelService);

  const persistedCollapsed = useLiveData(
    navigationPanelService.collapsed$(path)
  );
  const accordionSection = useRegisterNavigationPanelAccordionSection(
    accordion ? (path[0] as NavigationPanelRootSection) : undefined
  );
  const collapsed = accordionSection?.collapsed ?? persistedCollapsed;

  const setCollapsed = useCallback(
    (v: boolean) => {
      if (accordionSection) {
        if (!v) {
          navigationPanelService.activateRootSection(
            path[0] as NavigationPanelRootSection
          );
        }
        return;
      }
      navigationPanelService.setCollapsed(path, v);
    },
    [accordionSection, navigationPanelService, path]
  );

  return (
    <Collapsible.Root
      data-collapsed={collapsed}
      data-accordion={accordion || undefined}
      className={clsx(root, className)}
      open={!collapsed}
      data-testid={testId}
    >
      <CategoryDivider
        data-testid={headerTestId}
        label={title}
        setCollapsed={setCollapsed}
        collapsed={collapsed}
        ref={headerRef}
        className={clsx(header, headerClassName)}
      >
        {actions}
      </CategoryDivider>
      <Collapsible.Content
        data-testid="collapsible-section-content"
        data-accordion={accordion || undefined}
        className={clsx(content, contentClassName)}
        style={contentStyle}
      >
        {children}
      </Collapsible.Content>
    </Collapsible.Root>
  );
};
